import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../SafeIcon';
import supabase from '../../supabase/supabase';

const { FiX, FiCheck, FiPackage, FiTag, FiSmartphone, FiMaximize2, FiFilter } = FiIcons;

const ProductsWidgetPopup = ({ onClose, onUpdate, currentStyles = {}, projectId }) => {
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    productCategory: currentStyles.productCategory || '',
    sortBy: currentStyles.sortBy || 'newest',
    columns: currentStyles.columns || 3,
    mobileColumns: currentStyles.mobileColumns || 1,
    showSaleBadge: currentStyles.showSaleBadge !== false,
    limit: currentStyles.limit || 6,
    fullWidth: currentStyles.fullWidth || false,
  });

  useEffect(() => {
    fetchCategories();
  }, [projectId]);

  // Update local state when currentStyles change
  useEffect(() => {
    setSettings({
      productCategory: currentStyles.productCategory || '',
      sortBy: currentStyles.sortBy || 'newest',
      columns: currentStyles.columns || 3,
      mobileColumns: currentStyles.mobileColumns || 1,
      showSaleBadge: currentStyles.showSaleBadge !== false,
      limit: currentStyles.limit || 6,
      fullWidth: currentStyles.fullWidth || false,
    });
  }, [currentStyles]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('store_products_1772955957776')
        .select('category')
        .eq('is_active', true);

      if (projectId) {
        query = query.eq('project_id', projectId);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching categories:', error);
        setAllCategories([]);
        return;
      }

      const cats = Array.from(
        new Set(
          (data || [])
            .flatMap(p => (p.category || '').split(',').map(c => c.trim()).filter(Boolean))
        )
      ).sort();
      
      setAllCategories(cats);
    } catch (e) {
      console.error('Error in fetchCategories:', e);
      setAllCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    const updatedSettings = {
      productCategory: settings.productCategory,
      sortBy: settings.sortBy,
      columns: settings.columns,
      mobileColumns: settings.mobileColumns,
      showSaleBadge: settings.showSaleBadge,
      limit: settings.limit,
      fullWidth: settings.fullWidth,
    };
    
    onUpdate(updatedSettings);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiPackage} className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800">Products Widget</h3>
              <p className="text-[10px] text-gray-400 uppercase tracking-widest">Configure settings</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors text-gray-400">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Category */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center mb-3">
              <SafeIcon icon={FiTag} className="mr-2" />
              Product Category
            </label>
            {loading ? (
              <div className="flex items-center space-x-2 text-gray-400 text-sm">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                <span>Loading categories...</span>
              </div>
            ) : allCategories.length === 0 ? (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                {projectId 
                  ? 'No categories found in this project. Add products with categories in your store first.'
                  : 'No categories found. Add products with categories to your store.'}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSettings(s => ({ ...s, productCategory: '' }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                    settings.productCategory === ''
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  All Products
                </button>
                {allCategories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSettings(s => ({ ...s, productCategory: cat }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                      settings.productCategory === cat
                        ? 'bg-[#5494d4] text-white border-[#5494d4]'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#5494d4] hover:text-[#5494d4]'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sort By */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center mb-3">
              <SafeIcon icon={FiFilter} className="mr-2" />
              Sort Products By
            </label>
            <select
              value={settings.sortBy}
              onChange={e => setSettings(s => ({ ...s, sortBy: e.target.value }))}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#5494d4]/20 focus:border-[#5494d4] text-sm font-semibold"
            >
              <option value="newest">Newest Added</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="alphabetical">Alphabetical (A-Z)</option>
            </select>
          </div>

          {/* Full Width Toggle */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center mb-3">
              <SafeIcon icon={FiMaximize2} className="mr-2" />
              Width Settings
            </label>
            <label className="flex items-center space-x-3 cursor-pointer group w-max">
              <div
                onClick={() => setSettings(s => ({ ...s, fullWidth: !s.fullWidth }))}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                  settings.fullWidth ? 'bg-[#5494d4]' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    settings.fullWidth ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </div>
              <span className="text-sm text-gray-700">Full Width (Edge to Edge)</span>
            </label>
            <p className="text-[10px] text-gray-400 mt-1.5 ml-14">
              When enabled, products will span the full container width.
            </p>
          </div>

          {/* Desktop Columns */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
              Desktop Columns
            </label>
            <div className="flex space-x-2">
              {[1, 2, 3, 4].map(n => (
                <button
                  key={n}
                  onClick={() => setSettings(s => ({ ...s, columns: n }))}
                  className={`flex-1 py-2.5 rounded-xl font-black text-sm border-2 transition-all ${
                    settings.columns === n
                      ? 'bg-[#5494d4] text-white border-[#5494d4]'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Layout */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center mb-3">
              <SafeIcon icon={FiSmartphone} className="mr-2" />
              Mobile Columns
            </label>
            <div className="flex space-x-2">
              {[1, 2].map(n => (
                <button
                  key={n}
                  onClick={() => setSettings(s => ({ ...s, mobileColumns: n }))}
                  className={`flex-1 py-2.5 rounded-xl font-black text-sm border-2 transition-all ${
                    settings.mobileColumns === n
                      ? 'bg-[#5494d4] text-white border-[#5494d4]'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Max Products */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
              Max Products to Show
            </label>
            <select
              value={settings.limit}
              onChange={e => setSettings(s => ({ ...s, limit: Number(e.target.value) }))}
              className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-[#5494d4]/20 focus:border-[#5494d4] text-sm font-semibold"
            >
              {[3, 4, 6, 8, 12, 16, 20, 24].map(n => (
                <option key={n} value={n}>{n} products</option>
              ))}
            </select>
          </div>

          {/* Sale Badge Toggle */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">
              Sale Settings
            </label>
            <label className="flex items-center space-x-3 cursor-pointer group w-max">
              <div
                onClick={() => setSettings(s => ({ ...s, showSaleBadge: !s.showSaleBadge }))}
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                  settings.showSaleBadge ? 'bg-[#5494d4]' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    settings.showSaleBadge ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </div>
              <span className="text-sm text-gray-700">Show Sale / Discount Badges</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 bg-gray-50 border-t border-gray-100 flex space-x-3 shrink-0">
          <button onClick={onClose} className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-xl transition-colors text-sm">
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-[2] py-3 bg-[#5494d4] text-white font-bold rounded-xl hover:bg-blue-500 transition-colors text-sm flex items-center justify-center space-x-2"
          >
            <SafeIcon icon={FiCheck} className="w-4 h-4" />
            <span>Apply Settings</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ProductsWidgetPopup;